import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha, Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useNavigate } from 'react-router-dom';
import { useCurrentPlan } from 'src/Common/hooks/useCurrentPlan';
import {
  evaluateSubscription,
  snapshotFromCurrentPlan,
  type SubscriptionNoticeTone,
} from 'src/Common/subscription/subscriptionStatus';

// =====================================================================================
// "Subscription expiring soon" bar for the web back office — the same notice the desktop till
// shows (same rules, same wording: src/Common/subscription/subscriptionStatus.ts):
//   • free trial → always (info; warning in the last 3 days)
//   • paid plan  → from 15 days before the end (warning)
//   • expired    → error, cannot be dismissed
// The plan comes from the shared useCurrentPlan cache (the sidebar's call — no extra request).
// Dismissing hides it for the rest of the day; it comes back tomorrow (and whenever the
// number of days left changes).
// =====================================================================================

const DISMISS_KEY = 'subscriptionNoticeDismissed';
const RECHECK_MS = 60 * 1000;
const REFETCH_MS = 30 * 60 * 1000;
const BILLING_ROLES = ['StoreOwner', 'CompanyAdmin', 'Admin', 'Developer'];

function readCompanyId(): string | undefined {
  try {
    const raw = localStorage.getItem('employeeProfile');
    const id = raw ? JSON.parse(raw)?.companyId : undefined;
    return id === undefined || id === null || id === '' ? undefined : String(id);
  } catch {
    return undefined;
  }
}

function canManageBilling(): boolean {
  try {
    const roles = (localStorage.getItem('userRoles') || '').split(',').map((r) => r.trim());
    return roles.some((r) => BILLING_ROLES.includes(r));
  } catch {
    return false;
  }
}

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

const SubscriptionNoticeBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dark = theme.palette.mode === 'dark';
  const [companyId] = useState(readCompanyId);
  const { currentPlan, refresh } = useCurrentPlan(companyId);

  // When the plan answer arrived on this PC's clock (to age "days left" while the page stays open).
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  useEffect(() => {
    setReceivedAt(Date.now());
  }, [currentPlan]);

  // Re-evaluate every minute; re-fetch the plan every 30 minutes (renewals show up without a reload).
  // `refresh` is a new function each render, so the interval calls the latest one through a ref.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), RECHECK_MS);
    const refetch = window.setInterval(() => {
      void refreshRef.current();
    }, REFETCH_MS);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(refetch);
    };
  }, []);

  const view = useMemo(() => {
    if (!companyId || !currentPlan) return null;
    const snapshot = snapshotFromCurrentPlan(currentPlan, companyId, receivedAt);
    if (!snapshot) return null;
    return evaluateSubscription(snapshot, now + snapshot.serverOffsetMs);
  }, [companyId, currentPlan, receivedAt, now]);

  const dismissValue = view ? `${todayKey()}|${view.daysLeft ?? 'x'}` : '';
  const [dismissed, setDismissed] = useState<string | null>(() => {
    try {
      return localStorage.getItem(`${DISMISS_KEY}:${companyId ?? ''}`);
    } catch {
      return null;
    }
  });

  if (!view || !view.notice) return null;
  const tone: SubscriptionNoticeTone = view.notice.tone;
  const canDismiss = tone !== 'error';
  if (canDismiss && dismissed === dismissValue) return null;

  const palette = {
    info: { fg: dark ? '#84CAFF' : '#175CD3', bg: dark ? alpha('#84CAFF', 0.1) : '#EFF8FF', border: dark ? alpha('#84CAFF', 0.3) : '#B2DDFF' },
    warning: { fg: dark ? '#FDB022' : '#B54708', bg: dark ? alpha('#FDB022', 0.1) : '#FFFAEB', border: dark ? alpha('#FDB022', 0.3) : '#FEDF89' },
    error: { fg: dark ? '#F97066' : '#B42318', bg: dark ? alpha('#F97066', 0.1) : '#FEF3F2', border: dark ? alpha('#F97066', 0.3) : '#FECDCA' },
  }[tone];
  const Icon = tone === 'error' ? ErrorOutlineRoundedIcon : tone === 'warning' ? WarningAmberRoundedIcon : InfoOutlinedIcon;
  const showAction = canManageBilling();
  const actionLabel = view.isTrial ? 'Upgrade plan' : 'Renew now';
  const actionPath = view.isTrial ? '/subscriptions/upgrade' : '/management/billing';

  const dismiss = () => {
    try {
      localStorage.setItem(`${DISMISS_KEY}:${companyId ?? ''}`, dismissValue);
    } catch {
      /* storage blocked — hide for this page view only */
    }
    setDismissed(dismissValue);
  };

  return (
    <Box
      role={tone === 'error' ? 'alert' : 'status'}
      data-testid="subscription-notice-bar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 2, sm: 3, md: 4 },
        py: 1,
        bgcolor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        color: palette.fg,
        '@media print': { display: 'none' },
      }}
    >
      <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
      <Typography sx={{ flex: '1 1 240px', minWidth: 0, fontSize: 13.5, lineHeight: '20px', fontWeight: 500, color: palette.fg }}>
        {view.notice.message}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 'auto', sm: 0 }, flexShrink: 0 }}>
        {showAction && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(actionPath)}
            sx={{
              color: palette.fg,
              borderColor: palette.border,
              bgcolor: 'transparent',
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: palette.fg, bgcolor: alpha(palette.fg, 0.06) },
            }}
          >
            {actionLabel}
          </Button>
        )}
        {canDismiss && (
          <IconButton
            size="small"
            onClick={dismiss}
            aria-label="Hide for today"
            title="Hide for today"
            sx={{ color: palette.fg, '&:hover': { bgcolor: alpha(palette.fg, 0.08) } }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionNoticeBar;
